import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Users, Star, Clock, ArrowRight, Zap, Globe, Shield, TrendingUp, Award } from 'lucide-react';
import { projectService } from '../services/api';
import { MatchedResponse, Freelancer, Tag } from '../types';
import { useAuth } from '../contexts/AuthContext';

const Home: React.FC = () => {
  const { user } = useAuth();
  const [projectDescription, setProjectDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<MatchedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectDescription.trim()) return;

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await projectService.match({ description: projectDescription });
      setResults(response);
    } catch (err) {
      setError('Failed to find matching freelancers. Please try again.');
      console.error('Error matching project:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const FreelancerCard: React.FC<{ freelancer: Freelancer }> = ({ freelancer }) => (
    <Link to={`/freelancer/${freelancer.id}`} className="block group">
      <div className="relative overflow-hidden glass-card rounded-2xl p-6 border border-white/10 hover:border-secondary-400/30 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-secondary-400/10">
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
        
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-white truncate group-hover:text-secondary-400 transition-colors duration-300">
                {freelancer.name}
              </h3>
              <p className="text-sm text-white/60 truncate">{freelancer.email}</p>
            </div>
            <div className="flex items-center space-x-3 ml-4">
              <div className="text-right">
                <span className="text-lg font-bold text-secondary-400">
                  ${freelancer.cost_per_project}
                </span>
                <span className="text-xs text-white/60 block">/project</span>
              </div>
              <div className={`w-3 h-3 rounded-full ${freelancer.availability ? 'bg-secondary-400 animate-pulse shadow-lg shadow-secondary-400/50' : 'bg-red-400'}`} />
            </div>
          </div>

          <div className="mb-4 space-y-2">
            <div className="flex items-center space-x-2">
              <Clock size={16} className="text-white/50 flex-shrink-0" />
              <span className="text-sm text-white/70">
                {freelancer.experience_years} years experience
              </span>
            </div>
            {freelancer.relevance_score && (
              <div className="flex items-center space-x-2">
                <Star size={16} className="text-secondary-400 flex-shrink-0" />
                <span className="text-sm text-white/70">
                  {Math.round(freelancer.relevance_score * 100)}% match
                </span>
              </div>
            )}
          </div>

          {freelancer.bio && (
            <p className="text-sm text-white/80 mb-4 line-clamp-2 leading-relaxed">{freelancer.bio}</p>
          )}

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-white mb-3">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {freelancer.skills.slice(0, 4).map((skill, index) => (
                <span
                  key={index}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all duration-300 ${
                    freelancer.matched_skills?.includes(skill)
                      ? 'bg-secondary-400/20 text-secondary-400 border border-secondary-400/30'
                      : 'bg-white/10 text-white/70 border border-white/20'
                  }`}
                >
                  {skill}
                </span>
              ))}
              {freelancer.skills.length > 4 && (
                <span className="px-3 py-1.5 text-xs font-medium rounded-full bg-white/10 text-white/70 border border-white/20">
                  +{freelancer.skills.length - 4}
                </span>
              )}
            </div>
          </div>

          {freelancer.portfolio_url && (
            <div className="flex items-center justify-between">
              <a
                href={freelancer.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary-400 hover:text-secondary-300 text-sm font-medium transition-colors duration-300 flex items-center space-x-1"
                onClick={(e) => e.stopPropagation()}
              >
                <span>View Portfolio</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          )}
        </div>
      </div>
    </Link>
  );

  const SkillTag: React.FC<{ tag: Tag }> = ({ tag }) => (
    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-secondary-400/20 text-secondary-400 border border-secondary-400/30 hover:bg-secondary-400/30 transition-all duration-300">
      <span className="truncate max-w-32">{tag.tag}</span>
      <span className="ml-2 text-xs opacity-75 bg-secondary-400/20 px-2 py-0.5 rounded-full">
        {Math.round(tag.weight * 100)}%
      </span>
    </span>
  );

  const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string; delay: number }> = ({ 
    icon, title, description, delay 
  }) => (
    <div 
      className="group relative overflow-hidden glass-card rounded-2xl p-8 border border-white/10 hover:border-secondary-400/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-secondary-400/10"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary-400/5 via-transparent to-secondary-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      <div className="relative z-10">
        <div className="w-16 h-16 glass-secondary rounded-2xl flex items-center justify-center mx-auto mb-6 border border-secondary-400/30 group-hover:scale-110 transition-transform duration-500">
          <div className="text-secondary-400 group-hover:text-white transition-colors duration-300">
            {icon}
          </div>
        </div>
        <h3 className="text-xl font-bold text-white mb-4 text-center group-hover:text-secondary-400 transition-colors duration-300">
          {title}
        </h3>
        <p className="text-white/70 text-center leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {/* Constellation Background Animation */}
      
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-5 lg:py-5">                
            <div className="relative z-10 max-w-4xl mx-auto text-center space-y-12">
              {/* Enhanced Main Headline */}
              <div className="space-y-5">
                <div className="mb-6">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight tracking-tight">
                    RENDER
                    <span className="text-gradient bg-gradient-to-r from-secondary-400 to-secondary-600 bg-clip-text text-transparent">
                      SPACE
                    </span>
                  </h1>
                </div>
                <p className="text-xl sm:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed">
                  The freelance platform that keeps your flow with 
                  <span className="text-secondary-400 font-semibold"> AI-powered matching</span> and 
                  <span className="text-secondary-400 font-semibold"> seamless collaboration</span>
                </p>
                <div className="flex items-center justify-center space-x-6 text-sm text-white/60">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-secondary-400" />
                    <span>AI-Powered Matching</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-secondary-400" />
                    <span>Verified Talent</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Shield className="w-4 h-4 text-secondary-400" />
                    <span>Secure Payments</span>
                  </div>
                </div>
              </div>

              {/* Enhanced Search Section */}
              <div className="glass-card rounded-3xl p-8 border border-white/10 shadow-2xl backdrop-blur-xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="project-description" className="block text-lg font-semibold text-white mb-3">
                      Describe your project
                    </label>
                    <textarea
                      id="project-description"
                      rows={4}
                      className="w-full px-6 py-4 rounded-2xl text-base bg-white/5 border border-white/10 focus:border-secondary-400/50 focus:bg-white/10 transition-all duration-300 placeholder-white/50"
                      placeholder="Tell us about your project requirements, skills needed, timeline, budget, and any other details that will help us find the perfect match..."
                      value={projectDescription}
                      onChange={(e) => setProjectDescription(e.target.value)}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !projectDescription.trim()}
                    className="w-full btn-modern font-bold py-4 px-8 rounded-2xl text-lg transition-all duration-500 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed group hover:scale-[1.02]"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-secondary-400"></div>
                        <span>Finding perfect matches...</span>
                      </>
                    ) : (
                      <>
                        <Search className="w-5 h-5" />
                        <span>Find Freelancers</span>
                        <ArrowRight size={20} className="group-hover:translate-x-2 transition-transform duration-300" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="glass-card border-red-400/30 rounded-2xl p-6 bg-red-400/10">
            <p className="text-red-400 text-base">{error}</p>
          </div>
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Top Matching Freelancers 
              <span className="text-secondary-400 ml-2">({results.matched_freelancers.length})</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {results.matched_freelancers.map((freelancer, index) => (
                <FreelancerCard key={freelancer.id || index} freelancer={freelancer} />
              ))}
            </div>
            {results.matched_freelancers.length === 0 && (
              <div className="text-center py-16">
                <Users size={64} className="mx-auto text-white/40 mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">No matches found</h3>
                <p className="text-white/60 text-lg max-w-md mx-auto">
                  Try adjusting your project description or requirements to find better matches.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Features Section */}
      {!results && !isLoading && (
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              Why Choose <span className="text-gradient">RenderSpace</span>?
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
              Our AI-powered platform revolutionizes how you connect with talent and projects
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Zap size={32} />}
              title="Smart AI Matching"
              description="Our advanced AI analyzes your requirements and matches you with the most suitable opportunities based on skills, experience, and project fit."
              delay={0}
            />
            <FeatureCard
              icon={<Globe size={32} />}
              title="Global Talent Pool"
              description="Access verified freelancers from around the world with proven skills, portfolios, and client reviews."
              delay={200}
            />
            <FeatureCard
              icon={<Shield size={32} />}
              title="Secure & Reliable"
              description="Clear payment terms, secure escrow, and built-in project management tools for your peace of mind."
              delay={400}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;

